import { ButtonProps } from '@mui/material';
import { alpha, Theme } from '@mui/material/styles';

// ----------------------------------------------------------------------

const COLORS = ['primary', 'secondary', 'info', 'success', 'warning', 'error'] as const;

// NEW VARIANT
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    soft: true;
    glass: true;
  }
}

export default function Button(theme: Theme) {
  const isLight = theme.palette.mode === 'light';

  const rootStyle = (ownerState: ButtonProps) => {
    const inheritColor = ownerState.color === 'inherit';

    const containedVariant = ownerState.variant === 'contained';

    const outlinedVariant = ownerState.variant === 'outlined';

    const textVariant = ownerState.variant === 'text';

    const softVariant = ownerState.variant === 'soft';

    const glassVariant = ownerState.variant === 'glass';

    const smallSize = ownerState.size === 'small';

    const largeSize = ownerState.size === 'large';

    const defaultStyle = {
      ...(inheritColor && {
        // CONTAINED
        ...(containedVariant && {
          color: theme.palette.grey[800],
          '&:hover': {
            boxShadow: theme.customShadows.z8,
            backgroundColor: theme.palette.grey[400],
          },
        }),
        // OUTLINED
        ...(outlinedVariant && {
          borderColor: alpha(theme.palette.grey[500], 0.32),
          '&:hover': {
            borderColor: theme.palette.text.primary,
            backgroundColor: theme.palette.action.hover,
          },
        }),
        // TEXT
        ...(textVariant && {
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }),
        // SOFT
        ...(softVariant && {
          color: theme.palette.text.primary,
          backgroundColor: alpha(theme.palette.grey[500], 0.08),
          '&:hover': {
            backgroundColor: alpha(theme.palette.grey[500], 0.24),
          },
        }),
        // GLASS
        ...(glassVariant && {
          // backgroundColor: alpha(theme.palette.grey[500], 0.08),
          color: isLight ? theme.palette.primary.main : theme.palette.primary.light,
          backgroundColor: alpha(theme.palette.background.paper, 0.25), // 투명도 증가
          '&:hover': {
            backgroundColor: alpha(theme.palette.grey[500], 0.24),
          },
        }),
      }),
    };

    const colorStyle = COLORS.map((color) => ({
      ...(ownerState.color === color && {
        // CONTAINED
        ...(containedVariant && {
          '&:hover': {
            // boxShadow: theme.customShadows[color],
          },
        }),
        // SOFT
        ...(softVariant && {
          color: theme.palette[color][isLight ? 'dark' : 'light'],
          backgroundColor: alpha(theme.palette[color].main, 0.16),
          '&:hover': {
            backgroundColor: alpha(theme.palette[color].main, 0.32),
          },
        }),
        // GLASS
        ...(glassVariant && {
          color: theme.palette[color][isLight ? 'dark' : 'light'],
          backgroundColor: alpha(theme.palette[color].main, 0.16),
          // color: isLight ? theme.palette.primary.main : theme.palette.primary.light,
          // backgroundColor: alpha(theme.palette.background.paper, 0.25), // 투명도 증가
          backdropFilter: 'blur(20px) saturate(180%)', // saturate 추가로 색상 강화
          WebkitBackdropFilter: 'blur(20px) saturate(180%)', // Safari 지원
          border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`, // 테두리 더 선명하게
          borderRadius: '50px 50px 50px 50px',
          boxShadow: `
            0 8px 32px 0 ${alpha(theme.palette.common.black, 0.15)},
            inset 0 1px 0 0 ${alpha(theme.palette.common.white, 0.4)}
          `,
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-50%',
            width: '50%',
            height: '100%',
            background: `linear-gradient(90deg, transparent, ${alpha(
              theme.palette.common.white,
              0.4,
            )}, transparent)`,
            transform: 'skewX(-25deg)',
            transition: 'left 0.6s ease',
          },
          '&:hover': {
            backgroundColor: alpha(theme.palette.background.paper, 0.4),
            border: `1px solid ${alpha(theme.palette.common.white, 0.4)}`,
            boxShadow: `
              0 12px 40px 0 ${alpha(theme.palette.common.black, 0.2)},
              inset 0 1px 0 0 ${alpha(theme.palette.common.white, 0.5)}
            `,
            transform: 'translateY(-2px)',
            '&::before': {
              left: '100%', // 호버 시 반사 효과 애니메이션
            },
          },
          '&:active': {
            transform: 'translateY(0px)',
          },
        }),
      }),
    }));

    const disabledState = {
      '&.Mui-disabled': {
        // SOFT
        ...(softVariant && {
          backgroundColor: theme.palette.action.disabledBackground,
        }),
      },
    };

    const size = {
      ...(smallSize && {
        height: 38,
        fontSize: 13,
        // ...(softVariant && {
        padding: '4px 14px',
        // }),
      }),
      ...(largeSize && {
        height: 48,
        fontSize: 15,
        ...(softVariant && {
          padding: '8px 22px',
        }),
      }),
    };

    return [...colorStyle, defaultStyle, disabledState, size];
  };

  return {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: ({ ownerState }: { ownerState: ButtonProps }) => rootStyle(ownerState),
      },
    },
  };
}
